import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { locationAPI } from '../../services/api';
import MasterDataBulkUploadDialog from '../components/MasterDataBulkUploadDialog';

const DistrictManagement = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [talukaBulkOpen, setTalukaBulkOpen] = useState(false);

  // District dialog state
  const [districtDialog, setDistrictDialog] = useState({ open: false, mode: 'create', data: null });
  const [districtForm, setDistrictForm] = useState({
    name_mr: '',
    name_en: '',
    code: '',
    is_active: true,
  });

  // Taluka dialog state
  const [talukaDialog, setTalukaDialog] = useState({ open: false, mode: 'create', data: null, districtId: null });
  const [talukaForm, setTalukaForm] = useState({
    name_mr: '',
    name_en: '',
    code: '',
    district_id: '',
    is_active: true,
  });

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await locationAPI.getDistricts();
      setDistricts(response.data);
    } catch (err) {
      console.error('Error fetching districts:', err);
      setError('जिल्हे लोड करताना त्रुटी आली');
    } finally {
      setLoading(false);
    }
  };

  // District CRUD operations
  const handleOpenDistrictDialog = (mode, district = null) => {
    if (mode === 'edit' && district) {
      setDistrictForm({
        name_mr: district.name_mr,
        name_en: district.name_en,
        code: district.code,
        is_active: district.is_active,
      });
      setDistrictDialog({ open: true, mode, data: district });
    } else {
      setDistrictForm({ name_mr: '', name_en: '', code: '', is_active: true });
      setDistrictDialog({ open: true, mode: 'create', data: null });
    }
  };

  const handleCloseDistrictDialog = () => {
    setDistrictDialog({ open: false, mode: 'create', data: null });
    setDistrictForm({ name_mr: '', name_en: '', code: '', is_active: true });
  };

  const handleDistrictSubmit = async () => {
    setError('');
    setSuccess('');
    try {
      if (districtDialog.mode === 'create') {
        await locationAPI.createDistrict(districtForm);
        setSuccess('जिल्हा यशस्वीरित्या जोडला');
      } else {
        const districtId = districtDialog.data._id || districtDialog.data.id;
        await locationAPI.updateDistrict(districtId, districtForm);
        setSuccess('जिल्हा यशस्वीरित्या अद्यतनित केला');
      }
      handleCloseDistrictDialog();
      fetchDistricts();
    } catch (err) {
      console.error('Error saving district:', err);
      setError(err.response?.data?.detail || 'जिल्हा जतन करताना त्रुटी आली');
    }
  };

  const handleDeleteDistrict = async (districtId) => {
    if (!window.confirm('हा जिल्हा आणि त्याचे सर्व तालुके हटवायचे आहेत का?')) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      await locationAPI.deleteDistrict(districtId);
      setSuccess('जिल्हा यशस्वीरित्या हटवला');
      fetchDistricts();
    } catch (err) {
      console.error('Error deleting district:', err);
      setError(err.response?.data?.detail || 'जिल्हा हटवताना त्रुटी आली');
    }
  };

  const handleToggleDistrictStatus = async (district) => {
    setError('');
    try {
      const districtId = district._id || district.id;
      await locationAPI.updateDistrict(districtId, {
        name_mr: district.name_mr,
        name_en: district.name_en,
        code: district.code,
        is_active: !district.is_active,
      });
      setSuccess(`जिल्हा ${district.is_active ? 'निष्क्रिय' : 'सक्रिय'} केला`);
      fetchDistricts();
    } catch (err) {
      console.error('Error toggling district status:', err);
      setError('स्थिती बदलताना त्रुटी आली');
    }
  };

  // Taluka CRUD operations
  const handleOpenTalukaDialog = (mode, districtId, taluka = null) => {
    if (mode === 'edit' && taluka) {
      setTalukaForm({
        name_mr: taluka.name_mr,
        name_en: taluka.name_en,
        code: taluka.code,
        district_id: taluka.district_id,
        is_active: taluka.is_active,
      });
      setTalukaDialog({ open: true, mode, data: taluka, districtId });
    } else {
      setTalukaForm({ name_mr: '', name_en: '', code: '', district_id: districtId, is_active: true });
      setTalukaDialog({ open: true, mode: 'create', data: null, districtId });
    }
  };

  const handleCloseTalukaDialog = () => {
    setTalukaDialog({ open: false, mode: 'create', data: null, districtId: null });
    setTalukaForm({ name_mr: '', name_en: '', code: '', district_id: '', is_active: true });
  };

  const handleTalukaSubmit = async () => {
    setError('');
    setSuccess('');
    try {
      if (talukaDialog.mode === 'create') {
        await locationAPI.createTaluka(talukaForm);
        setSuccess('तालुका यशस्वीरित्या जोडला');
      } else {
        const talukaId = talukaDialog.data._id || talukaDialog.data.id;
        await locationAPI.updateTaluka(talukaId, {
          name_mr: talukaForm.name_mr,
          name_en: talukaForm.name_en,
          code: talukaForm.code,
          is_active: talukaForm.is_active,
        });
        setSuccess('तालुका यशस्वीरित्या अद्यतनित केला');
      }
      handleCloseTalukaDialog();
      fetchDistricts();
    } catch (err) {
      console.error('Error saving taluka:', err);
      setError(err.response?.data?.detail || 'तालुका जतन करताना त्रुटी आली');
    }
  };

  const handleDeleteTaluka = async (talukaId) => {
    if (!window.confirm('हा तालुका हटवायचा आहे का?')) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      await locationAPI.deleteTaluka(talukaId);
      setSuccess('तालुका यशस्वीरित्या हटवला');
      fetchDistricts();
    } catch (err) {
      console.error('Error deleting taluka:', err);
      setError(err.response?.data?.detail || 'तालुका हटवताना त्रुटी आली');
    }
  };

  const handleToggleTalukaStatus = async (taluka) => {
    setError('');
    try {
      const talukaId = taluka._id || taluka.id;
      await locationAPI.updateTaluka(talukaId, {
        name_mr: taluka.name_mr,
        name_en: taluka.name_en,
        code: taluka.code,
        is_active: !taluka.is_active,
      });
      setSuccess(`तालुका ${taluka.is_active ? 'निष्क्रिय' : 'सक्रिय'} केला`);
      fetchDistricts();
    } catch (err) {
      console.error('Error toggling taluka status:', err);
      setError('स्थिती बदलताना त्रुटी आली');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Typography variant="h4" component="h1">
          जिल्हे आणि तालुके व्यवस्थापन
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setTalukaBulkOpen(true)}
          >
            तालुका बल्क अपलोड
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDistrictDialog('create')}
          >
            नवीन जिल्हा जोडा
          </Button>
        </Box>
      </Box>

      <MasterDataBulkUploadDialog
        open={talukaBulkOpen}
        onClose={() => setTalukaBulkOpen(false)}
        onSuccess={fetchDistricts}
        title="तालुका बल्क अपलोड"
        columns={[
          { key: 'name_mr', required: true, example: 'अंबरनाथ' },
          { key: 'name_en', required: true, example: 'Ambarnath' },
          { key: 'code', required: true, example: 'THN-AMB' },
          { key: 'district', required: true, example: 'Thane' },
        ]}
        uploadFn={locationAPI.bulkUploadTalukas}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {districts.map((district) => {
            const districtId = district._id || district.id;
            return (
              <Accordion key={districtId} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center', width: '100%', pr: 2 }}>
                    <Typography sx={{ flexGrow: 1, fontWeight: 'bold', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {district.name_mr} ({district.name_en})
                    </Typography>
                    <Chip
                      label={district.is_active ? 'सक्रिय' : 'निष्क्रिय'}
                      color={district.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${district.talukas?.length || 0} तालुके`}
                      size="small"
                      sx={{ mr: 2 }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleDistrictStatus(district);
                      }}
                      title={district.is_active ? 'निष्क्रिय करा' : 'सक्रिय करा'}
                    >
                      {district.is_active ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDistrictDialog('edit', district);
                      }}
                      title="संपादित करा"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDistrict(districtId);
                      }}
                      title="हटवा"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">तालुके</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenTalukaDialog('create', districtId)}
                      >
                        नवीन तालुका जोडा
                      </Button>
                    </Box>
                  {district.talukas && district.talukas.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>मराठी नाव</TableCell>
                            <TableCell>English Name</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>स्थिती</TableCell>
                            <TableCell align="right">क्रिया</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {district.talukas.map((taluka) => {
                            const talukaId = taluka._id || taluka.id;
                            return (
                              <TableRow key={talukaId}>
                                <TableCell>{taluka.name_mr}</TableCell>
                                <TableCell>{taluka.name_en}</TableCell>
                                <TableCell>
                                  <code>{taluka.code}</code>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={taluka.is_active ? 'सक्रिय' : 'निष्क्रिय'}
                                    color={taluka.is_active ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleTalukaStatus(taluka)}
                                    title={taluka.is_active ? 'निष्क्रिय करा' : 'सक्रिय करा'}
                                  >
                                    {taluka.is_active ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenTalukaDialog('edit', district.id || district._id, taluka)}
                                    title="संपादित करा"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteTaluka(talukaId)}
                                    title="हटवा"
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      या जिल्ह्यात कोणतेही तालुके नाहीत
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
            );
          })}
          {districts.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                कोणतेही जिल्हे आढळले नाहीत. नवीन जिल्हा जोडण्यासाठी वरील बटणावर क्लिक करा.
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* District Dialog */}
      <Dialog open={districtDialog.open} onClose={handleCloseDistrictDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {districtDialog.mode === 'create' ? 'नवीन जिल्हा जोडा' : 'जिल्हा संपादित करा'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="मराठी नाव *"
                fullWidth
                value={districtForm.name_mr}
                onChange={(e) => setDistrictForm({ ...districtForm, name_mr: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="English Name *"
                fullWidth
                value={districtForm.name_en}
                onChange={(e) => setDistrictForm({ ...districtForm, name_en: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Code *"
                fullWidth
                value={districtForm.code}
                onChange={(e) => setDistrictForm({ ...districtForm, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                helperText="Unique code (e.g., pune, mumbai)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDistrictDialog}>रद्द करा</Button>
          <Button
            onClick={handleDistrictSubmit}
            variant="contained"
            disabled={!districtForm.name_mr || !districtForm.name_en || !districtForm.code}
          >
            {districtDialog.mode === 'create' ? 'जोडा' : 'अद्यतनित करा'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Taluka Dialog */}
      <Dialog open={talukaDialog.open} onClose={handleCloseTalukaDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {talukaDialog.mode === 'create' ? 'नवीन तालुका जोडा' : 'तालुका संपादित करा'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="मराठी नाव *"
                fullWidth
                value={talukaForm.name_mr}
                onChange={(e) => setTalukaForm({ ...talukaForm, name_mr: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="English Name *"
                fullWidth
                value={talukaForm.name_en}
                onChange={(e) => setTalukaForm({ ...talukaForm, name_en: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Code *"
                fullWidth
                value={talukaForm.code}
                onChange={(e) => setTalukaForm({ ...talukaForm, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                helperText="Unique code (e.g., pune_city, haveli)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTalukaDialog}>रद्द करा</Button>
          <Button
            onClick={handleTalukaSubmit}
            variant="contained"
            disabled={!talukaForm.name_mr || !talukaForm.name_en || !talukaForm.code}
          >
            {talukaDialog.mode === 'create' ? 'जोडा' : 'अद्यतनित करा'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DistrictManagement;
